import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Save } from "lucide-react";
import { api } from "@/lib/api";

const KINDS = [
  { value: "objeto", label: "Objeto de contrato" },
  { value: "forma_pagamento", label: "Forma de pagamento" },
  { value: "poderes", label: "Poderes específicos" },
  { value: "clausula", label: "Cláusula avulsa" },
];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("objeto");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const r = await api.get("/templates");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!title.trim() || !content.trim()) { toast.error("Preencha título e conteúdo"); return; }
    await api.post("/templates", { kind, title, content });
    setTitle(""); setContent("");
    toast.success("Modelo salvo");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Excluir modelo?")) return;
    await api.delete(`/templates/${id}`);
    load();
  };

  const copy = (t) => {
    navigator.clipboard.writeText(t.content);
    toast.success("Conteúdo copiado");
  };

  const shown = filter === "all" ? items : items.filter(i => i.kind === filter);

  return (
    <div className="flex-1 min-w-0 flex flex-col p-3 sm:p-6 lg:p-8 font-sans">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-semibold text-slate-900">Modelos & Cláusulas</h1>
        <p className="text-slate-500 mt-1">Salve cláusulas, objetos e formas de pagamento reutilizáveis. Aparecerão como sugestões nos formulários.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <section className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-6 h-fit">
          <h2 className="font-heading text-lg font-semibold mb-4">Novo modelo</h2>
          <div className="space-y-4">
            <label className="block">
              <div className="text-xs font-medium text-slate-600 mb-1">Tipo</div>
              <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="template-kind"
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-xs font-medium text-slate-600 mb-1">Título</div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="template-title"
                placeholder="Ex.: Mandado de Segurança - matrícula UFU"
                className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
            </label>
            <label className="block">
              <div className="text-xs font-medium text-slate-600 mb-1">Conteúdo</div>
              <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={6} data-testid="template-content"
                placeholder="Escreva o texto exatamente como deverá aparecer no contrato..."
                className="w-full p-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-slate-900 outline-none text-sm"/>
            </label>
            <button onClick={create} data-testid="btn-create-template"
              className="w-full gradient-primary text-white h-11 rounded-xl inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition text-sm font-medium">
              <Save className="w-4 h-4"/> Salvar modelo
            </button>
          </div>
        </section>

        <section className="lg:col-span-3">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {[{value:"all",label:"Todos"}, ...KINDS].map(k => (
              <button key={k.value} onClick={() => setFilter(k.value)} data-testid={`filter-${k.value}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${filter===k.value?'bg-slate-900 text-white border-slate-900':'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
                {k.label}
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-500 text-sm">
              Nenhum modelo salvo ainda.
            </div>
          ) : (
            <ul className="space-y-3">
              {shown.map(t => (
                <li key={t.id} className="bg-white rounded-2xl border border-slate-100 p-5" data-testid={`template-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] uppercase tracking-wider text-[#0f4c5c] font-semibold">
                          {KINDS.find(k => k.value === t.kind)?.label || t.kind}
                        </span>
                      </div>
                      <div className="font-heading text-base font-semibold text-slate-900">{t.title}</div>
                      <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap line-clamp-4">{t.content}</p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => copy(t)} className="p-2 rounded-lg hover:bg-slate-50 border border-slate-100 text-slate-600" title="Copiar" data-testid={`copy-${t.id}`}><Copy className="w-4 h-4"/></button>
                      <button onClick={() => del(t.id)} className="p-2 rounded-lg hover:bg-red-50 border border-slate-100 text-red-500" title="Excluir" data-testid={`del-${t.id}`}><Trash2 className="w-4 h-4"/></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
